import {
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule } from '@angular/material/paginator';
import {
  Attachment,
  Dataset,
  ItemListResponse,
  Permission,
} from '@models';
import { AttachmentService, AuthService, NotificationService } from '@services';
import { AttachmentItemComponent } from '../item/item.component';
import { environment } from '@environment';
import { AuthorizeDirective, StopPropagationDirective } from '@directives';
import { CommonModule, DatePipe } from '@angular/common';
import { HumanReadableSizePipe } from '@pipes';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { AttachmentSearchComponent } from '@components/attachment/search/search.component';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpParams, httpResource } from '@angular/common/http';
import { ListComponent } from '@components/list/list.component';
import { DateAdapter } from '@angular/material/core';

interface Directory {
  title: string;
  path: string;
  prev: string;
}

@Component({
  selector: 'app-attachment-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,

    MatListModule,
    MatPaginatorModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatChipsModule,
    MatMenuModule,

    ListComponent,
    AuthorizeDirective,
    StopPropagationDirective,
    HumanReadableSizePipe,
  ],
})
export class AttachmentListComponent {
  currentDateString = new Date().toISOString();
  hostUrl = environment.kinkoHost;

  dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  route = inject(ActivatedRoute);
  router = inject(Router);
  attachmentService = inject(AttachmentService);
  notificationService = inject(NotificationService);
  dialog = inject(MatDialog);
  authService = inject(AuthService);

  dataset = signal<Dataset>(this.route.snapshot.parent!.data['dataset']);
  permissions = signal<Permission>(this.route.snapshot.parent!.data['permissions']);

  prefix = signal(this.route.snapshot.queryParams['prefix'] || '/');
  page = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  pageSize = signal(parseInt(this.route.snapshot.queryParams['pageSize']) || 10);
  q = signal(this.route.snapshot.queryParams['q'] || '');
  from = signal<Date | null>(this.parseDate(this.route.snapshot.queryParams['from']));
  until = signal<Date | null>(this.parseDate(this.route.snapshot.queryParams['until']));
  order = signal<string | undefined>(this.route.snapshot.queryParams['order']);
  count = signal(0);

  archiveQueryParams = computed(() => {
    let params = new HttpParams()
      .set('zip', true)
      .set('recursive', true);

    const from = this.formatDate(this.from());
    if (from) {
      params = params.set('from', from);
    }

    const until = this.formatDate(this.until());
    if (until) {
      const dayAfterUntil = new Date(until).getTime() + 86400000;
      params = params.set('until', (new Date(dayAfterUntil)).toISOString());
    }

    if (this.q()) {
      params = params.set('q', this.q());
    }

    if (this.prefix()) {
      params = params.set('prefix', this.prefix());
    }

    return params.toString();
  });

  archiveInformation = httpResource<ItemListResponse<unknown>>(() =>
    `${environment.kinkoHost}/dataset/${this.dataset().id}/attachment/?${this.archiveQueryParams()}&count=true&take=0`);

  archiveUrl = computed(() => {
    return `${environment.kinkoHost}/dataset/${this.dataset().id}/attachment/_blob?${this.archiveQueryParams()}`;
  });

  attachments = httpResource<ItemListResponse<Attachment>>(() => {
    let params = new HttpParams()
      .set('skip', this.page() * this.pageSize())
      .set('take', this.pageSize());

    const order = this.order();
    if (order) {
      params = params.set('order', order);

      if (order.startsWith('title')) {
        params = params.append('order', order.replace('title', 'filename'));
      }
    } else {
      params = params.set('order', 'created:desc');
    }

    const recursive = !!(this.q() || this.from() || this.until());
    if (recursive) {
      params = params.set('recursive', true);
    } else {
      params = params.set('prefixes', true);
    }

    if (this.q()) {
      params = params.set('q', this.q());
    }

    const from = this.formatDate(this.from());
    if (from) {
      params = params.set('from', from);
    }

    const until = this.formatDate(this.until());
    if (until) {
      const dayAfterUntil = new Date(until).getTime() + 86400000;
      params = params.set('until', (new Date(dayAfterUntil)).toISOString());
    }

    if (this.prefix()) {
      params = params.set('prefix', this.prefix());
    }

    return `${environment.kinkoHost}/dataset/${this.dataset().id}/attachment/?${params.toString()}`;
  });

  paths = computed(() => {
    if (!this.attachments.hasValue()) {
      return [];
    }

    const currentPath = this.prefix();
    const paths = new Set<string>();

    for (const path of this.attachments.value()!.prefixes || []) {
      if (path === currentPath) {
        continue;
      }

      paths.add(
        path.substring(0, currentPath.length + path.substring(currentPath.length).indexOf('/') + 1)
      );
    }

    return Array.from(paths);
  });

  constructor() {
    this.route.queryParams.subscribe((params: Params) => {
      this.page.set(parseInt(params['page']) || 0);
      this.pageSize.set(parseInt(params['pageSize']) || 10);
      this.q.set(params['q'] as string || '');
      this.from.set(this.parseDate(params['from']));
      this.until.set(this.parseDate(params['until']));
      this.prefix.set(params['prefix'] as string || '/');
      this.order.set(params['order'] as string || undefined);
    });

    effect(() => {
      if (!this.archiveInformation.hasValue()) {
        return;
      }

      this.count.set(this.archiveInformation.value().count);
    });
  }

  open(attachment: Attachment) {
    this.dialog.open(AttachmentItemComponent, {
      data: {
        attachment: attachment,
        isReleased: this.isReleased(attachment),
        requiresAuthorization: this.requiresAuthorization(attachment),
        canDownload: this.canDownload(attachment)
      },
      minWidth: '360px',
      width: '480px',
    });
  }

  isReleased(attachment: Attachment): boolean {
    return !!attachment.released
      && attachment.released <= this.currentDateString;
  }

  requiresAuthorization(attachment: Attachment): boolean {
    return this.dataset().type === 'draft'
      || !this.isReleased(attachment);
  }

  canDownload(attachment: Attachment): boolean {
    return this.isReleased(attachment)
      || this.permissions()?.mayRead;
  }

  openSearch() {
    const dialogRef = this.dialog.open(AttachmentSearchComponent, {
      data: {
        search: this.q(),
        prefix: this.prefix(),
        from: this.from(),
        until: this.until(),
        order: this.order(),
      },
      minWidth: '360px',
      width: '480px',
    });

    dialogRef.afterClosed().subscribe(
      (
        data: {
          search: string;
          prefix: string;
          from: Date | null;
          until: Date | null;
          order?: string;
        } | null,
      ) => {
        if (!data) {
          return;
        }

        this.filter(0, this.pageSize(), data.search, data.prefix, data.from, data.until, data.order);
      },
    );
  }

  filter(
    pageIndex: number, pageSize: number,
    q: string, prefix: string,
    from: Date | null, until: Date | null,
    order?: string,
  ) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: pageIndex || undefined,
        pageSize: pageSize === 10 ? undefined : pageSize,
        q: q || undefined,
        prefix: prefix || undefined,
        from: this.formatDate(from),
        until: this.formatDate(until),
        order,
      },
      replaceUrl: true
    });
  }

  getDirectory(path: string): Directory | null {
    if (!path) {
      return null;
    }

    // TODO: improve performance
    const paths = path.slice(0, -1).split('/');
    const title = paths.pop() || '/';
    const prev = title === '/' ? undefined : paths.join("/") + "/";

    return {
      title: title,
      path: path,
      prev: prev,
    } as Directory;
  }

  private parseDate(s: string): Date | null {
    const d = new Date(s);
    return d.getTime() ? d : null;
  }

  private formatDate(d: Date | null | undefined): string | undefined {
    return d ? d.toISOString() : undefined
  }
}
